import { SITE } from '@/app/constants';
import type { SubscriptionInfo } from '@/app/stores/slices/user-info';
import type {
  ClawPassType,
  ClawUserVariant,
  CreditV1PeriodProduct,
  ProductPayStatusType,
  SubsPlanType,
} from '@/app/types';
import type { CreditV1ClawPassStatusReply } from '../services/credit';
import { isTeamPlan } from './subs-plan';

const IAP_PAY_CHANNELS = new Set(['apple', 'google']);

export const isIapSubscription = (subscription: SubscriptionInfo | null): boolean =>
  IAP_PAY_CHANNELS.has(subscription?.pay_channel ?? '');

// 付费试用只有 3 天一档，埋点上用它顶替 billing_period
const PAID_TRIAL_PERIOD = 'trial-3d';

/** 埋点 subscribe_type：本次购买的是试用时返回 'subscription-trial-3d' */
export const getSubscribeType = (product: CreditV1PeriodProduct, isPaidTrial: boolean): string => {
  const period = isPaidTrial ? PAID_TRIAL_PERIOD : product.billing_period;
  return `${product.product_type}-${period}`;
};

/** 埋点 subscribe_type_origin：变更前的订阅仍在试用期时返回 'subscription-trial-3d' */
export const getSubscribeTypeOrigin = (subscription: SubscriptionInfo | null | undefined): string => {
  const period = subscription?.is_paid_trialing ? PAID_TRIAL_PERIOD : subscription?.billing_period;
  return `${subscription?.product_type}-${period}`;
};

// 个人版套餐等级映射（不含 team_v2：企业版按"新购/次包"语义处理，不参与升降级判定）
// 当前订阅等级 < 变更等级 => upgrade
// 当前订阅等级 > 变更等级 => downgrade
// 当前订阅等级 = 变更等级 => change
type IndividualPlanType = Exclude<SubsPlanType, 'team_v2'>;
const PLAN_LEVEL_MAP: Record<IndividualPlanType, number> = {
  free: 0,
  standard_v2: 1,
  premium_v2: 2,
  ultimate_v2: 3,
};

export const getProductStatus = (
  product: CreditV1PeriodProduct,
  subscription: SubscriptionInfo | null = null,
  clawPass: ClawPassType | null = null,
): ProductPayStatusType => {
  // 如果有购买了clawpass的套餐, 则当前产品不可购买
  if (clawPass?.has_benefit === true && clawPass.type === 'claw_pass' && clawPass.product_type === 'claw_pass_bundle') {
    return 'unavailable';
  }

  // 企业版套餐：本函数仅用于个人版升降级判定，企业版状态由 useFetchProductList 单独赋值。
  // 这里防御性兜底为 'available'，避免误调用走到下方个人版升降级分支。
  if (isTeamPlan(product.plan_type as SubsPlanType)) return 'available';

  // 订阅id为空,全员为可购买
  if (!subscription || !subscription?.id) return 'available';
  // 订阅id不为空, 看当前产品是否为当前订阅产品
  if (subscription.product_id === product.id) {
    // 国内取消自动续费且非冷却期 → 继续订阅。
    if (
      SITE === 'china' &&
      subscription.auto_renew_canceled &&
      !subscription.advance_renew_in_cooldown &&
      !isIapSubscription(subscription)
    )
      return 'continue';
    return 'current';
  }

  // App 内购（Apple / Google）订阅无法在 Web 升降级或变更：订阅由 Apple/Google 托管，
  if (isIapSubscription(subscription)) return 'unavailable';

  // 当前产品不可以购买
  if (!product.available) return 'unavailable';

  // 个人版升降级判定。若当前订阅是企业版（team_v2），用户来看个人版套餐属于跨体系，按"新购"兜底。
  const currentLevel = PLAN_LEVEL_MAP[subscription.plan_type as IndividualPlanType];
  const changeLevel = PLAN_LEVEL_MAP[product.plan_type as IndividualPlanType];
  if (currentLevel === undefined || changeLevel === undefined) return 'available';
  return currentLevel < changeLevel ? 'upgrade' : currentLevel > changeLevel ? 'downgrade' : 'change';
};

export const getClawPassProductStatus = (product: CreditV1PeriodProduct): ProductPayStatusType => {
  return product.available === false ? 'unavailable' : 'available';
};

export const getClawUserVariant = (clawPass: CreditV1ClawPassStatusReply | null): ClawUserVariant => {
  if (clawPass?.has_benefit === true) {
    if (clawPass.type === 'claw_pass' || clawPass.type === 'granted') {
      return 'clawPass';
    }
    if (clawPass.type === 'claw_code' || clawPass.type === 'vidu') {
      return 'trial';
    }
  }
  return 'free';
};
